import { createContext, useContext, useState, useCallback, useRef, type ReactNode } from "react";

interface AppModalsContextValue {
  // Add Link Modal
  isAddLinkModalOpen: boolean;
  sharedUrl?: string;
  initialContextGroupId?: string | null;
  openAddLinkModal: (url?: string, initialContextGroupId?: string | null) => void;
  closeAddLinkModal: () => void;
  clearSharedUrl: () => void;

  // Auth Prompt Modal
  isAuthPromptOpen: boolean;
  openAuthPrompt: () => void;
  closeAuthPrompt: () => void;

  // Pending checkout (after auth)
  pendingCheckoutPriceId: 'pro_monthly' | 'pro_yearly' | 'lifetime' | null;
  openAuthPromptForCheckout: (priceId: 'pro_monthly' | 'pro_yearly' | 'lifetime') => void;
  clearPendingCheckout: () => void;

  // Install Prompt Modal
  isInstallPromptOpen: boolean;
  openInstallPrompt: () => void;
  closeInstallPrompt: () => void;

  // Install prompt tracking
  hasShownInstallPrompt: boolean;
  markInstallPromptShown: () => void;
}

const AppModalsContext = createContext<AppModalsContextValue | null>(null);

export const AppModalsProvider = ({ children }: { children: ReactNode }) => {
  const [isAddLinkModalOpen, setIsAddLinkModalOpen] = useState(false);
  const [isAuthPromptOpen, setIsAuthPromptOpen] = useState(false);
  const [isInstallPromptOpen, setIsInstallPromptOpen] = useState(false);
  const [sharedUrl, setSharedUrl] = useState<string | undefined>(undefined);
  const [initialContextGroupId, setInitialContextGroupId] = useState<string | null | undefined>(undefined);
  const [pendingCheckoutPriceId, setPendingCheckoutPriceId] = useState<'pro_monthly' | 'pro_yearly' | 'lifetime' | null>(null);

  const hasShownInstallPromptRef = useRef(false);

  const openAddLinkModal = useCallback((url?: string, contextGroupId?: string | null) => {
    setSharedUrl(url);
    setInitialContextGroupId(contextGroupId);
    setIsAddLinkModalOpen(true);
  }, []);

  const closeAddLinkModal = useCallback(() => {
    setIsAddLinkModalOpen(false);
    setTimeout(() => {
      setSharedUrl(undefined);
      setInitialContextGroupId(undefined);
    }, 300);
  }, []);

  const clearSharedUrl = useCallback(() => {
    setSharedUrl(undefined);
  }, []);

  const openAuthPrompt = useCallback(() => {
    setPendingCheckoutPriceId(null);
    setIsAuthPromptOpen(true);
  }, []);

  const openAuthPromptForCheckout = useCallback((priceId: 'pro_monthly' | 'pro_yearly' | 'lifetime') => {
    setPendingCheckoutPriceId(priceId);
    setIsAuthPromptOpen(true);
  }, []);

  const closeAuthPrompt = useCallback(() => {
    setIsAuthPromptOpen(false);
  }, []);

  const clearPendingCheckout = useCallback(() => {
    setPendingCheckoutPriceId(null);
  }, []);

  const openInstallPrompt = useCallback(() => {
    setIsInstallPromptOpen(true);
  }, []);

  const closeInstallPrompt = useCallback(() => {
    setIsInstallPromptOpen(false);
  }, []);

  const markInstallPromptShown = useCallback(() => {
    hasShownInstallPromptRef.current = true;
  }, []);

  const value: AppModalsContextValue = {
    isAddLinkModalOpen,
    sharedUrl,
    initialContextGroupId,
    openAddLinkModal,
    closeAddLinkModal,
    clearSharedUrl,
    isAuthPromptOpen,
    openAuthPrompt,
    openAuthPromptForCheckout,
    closeAuthPrompt,
    pendingCheckoutPriceId,
    clearPendingCheckout,
    isInstallPromptOpen,
    openInstallPrompt,
    closeInstallPrompt,
    hasShownInstallPrompt: hasShownInstallPromptRef.current,
    markInstallPromptShown,
  };

  return (
    <AppModalsContext.Provider value={value}>
      {children}
    </AppModalsContext.Provider>
  );
};

export const useAppModalsContext = () => {
  const ctx = useContext(AppModalsContext);
  if (!ctx) throw new Error("useAppModalsContext must be used within AppModalsProvider");
  return ctx;
};
